// useSettings Hook
// Thin wrapper around SettingsContext for backward compatibility
// Profile-related operations remain local as they don't need global settings sync

import { useAuth } from '../../lib/AuthContext';
import { useSettingsContext, UserSettings } from '../../lib/SettingsContext';
import { supabase } from '../../lib/supabase-client';
import { toast } from 'sonner';

// Re-export types for backward compatibility
export type { UserSettings } from '../../lib/SettingsContext';

export interface UserProfile {
    id: string;
    first_name: string;
    last_name: string;
    email: string;
    avatar_url: string | null;
    subscription_status: string;
    available_credits: number;
}

export function useSettings() {
    const { user, profile, refreshProfile } = useAuth();
    const { settings, loading, updateSettings, refreshSettings, uploadFile } = useSettingsContext();

    // Profile update - stays local (uses AuthContext's profile)
    async function updateProfile(updates: Partial<UserProfile>) {
        if (!user) return;
        try {
            const { error } = await supabase
                .from('profiles' as any)
                .update(updates)
                .eq('id', user.id);

            if (error) throw error;

            await refreshProfile();
            toast.success('Profil zaktualizowany');
        } catch (error) {
            console.error('Error updating profile:', error);
            toast.error('Błąd aktualizacji profilu');
        }
    }

    // Password update - stays local (uses Supabase Auth)
    async function updatePassword(newPassword: string) {
        if (!user) return;
        try {
            const { error } = await supabase.auth.updateUser({ password: newPassword });
            if (error) throw error;
            toast.success('Hasło zostało zmienione pomyślnie.');
        } catch (error: any) {
            console.error('Error updating password:', error);
            toast.error(error.message || 'Błąd zmiany hasła');
        }
    }

    return {
        // From AuthContext
        profile,

        // From SettingsContext (shared globally)
        settings,
        loading,
        updateSettings,
        refreshSettings,
        uploadFile,

        // Local operations
        updateProfile,
        updatePassword,
    };
}
